import type { PublicWalletTransfer, WalletDirection } from "../wallet-types";

// Shared by the wallet and game entry. Keep the v1 key/schema so old requests remain recoverable.
export type TransferIntent = { clientId: string; provider: string; direction: WalletDirection; amount: string; orderId?: string };
export const walletTransferLabels: Record<PublicWalletTransfer["status"], string> = { submitting: "自动确认中", pending: "自动确认中", succeeded: "转账成功", failed: "转账失败" };
export const storageKey = (userId: string) => `xh.wallet.intent.v1.${userId}`;
export const unresolved = (transfer: PublicWalletTransfer) => transfer.status === "submitting" || transfer.status === "pending";
export const isDurableWalletReview = (transfer?: PublicWalletTransfer | null) => transfer?.resolution?.outcome === "needs_review";
// A durable review result is a terminal quarantine: the same venue stays
// locked, but the browser must not keep polling or offer another supplier query.
export const queryableTransfer = (transfer?: PublicWalletTransfer | null) => Boolean(transfer && unresolved(transfer) && !isDurableWalletReview(transfer));
// A durable order is recoverable from the server ledger, so it only locks its
// own venue. A request with no server order id remains a global guard: replacing
// that sole local recovery key could orphan an outcome-unknown POST.
export const intentBlocksProvider = (intent: TransferIntent | null | undefined, provider: string) => Boolean(intent && (!intent.orderId || intent.provider === provider));
export const transferBlocksProvider = (transfer: PublicWalletTransfer | null | undefined, provider: string) => Boolean(transfer && unresolved(transfer) && transfer.provider === provider);
// Confirmation attention is a presentation hint, not a financial terminal state.
export const needsWalletReview = (transfer?: PublicWalletTransfer | null) => Boolean(transfer && unresolved(transfer) && (transfer.confirmationState === "needs_review" || isDurableWalletReview(transfer)));
export const walletTransferLabel = (transfer: PublicWalletTransfer) => isDurableWalletReview(transfer)
  ? "转账待人工核查"
  : transfer.status === "succeeded" && transfer.resolution?.outcome === "partial"
    ? transfer.direction === "deposit" ? "部分转入成功" : "部分转回成功"
    : needsWalletReview(transfer) ? "转账待核查" : walletTransferLabels[transfer.status];
export const walletReviewMessage = (transfer?: PublicWalletTransfer | null) => needsWalletReview(transfer) ? transfer?.message || "原转账结果仍待核查，请保留订单号查询原单，勿重复转账。" : "";

export type WalletSettlementSummary = {
  requestedAmountCents: number;
  actualAmountCents: number;
  differenceAmountCents: number;
  actualLabel: string;
  differenceLabel: string;
};

/** Returns display-safe values only for a valid partial terminal settlement. */
export function walletSettlementSummary(transfer?: PublicWalletTransfer | null): WalletSettlementSummary | null {
  if (!transfer || transfer.status !== "succeeded" || transfer.resolution?.outcome !== "partial") return null;
  const actual = transfer.resolution.actualAmountCents;
  if (!Number.isSafeInteger(actual) || actual === undefined || actual <= 0 || actual >= transfer.amountCents) return null;
  return {
    requestedAmountCents: transfer.amountCents,
    actualAmountCents: actual,
    differenceAmountCents: transfer.amountCents - actual,
    actualLabel: transfer.direction === "deposit" ? "实际转入" : "实际转回",
    differenceLabel: transfer.direction === "deposit" ? "退回平台" : "未转回差额",
  };
}


export function amountCents(value: string): number | null {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents : null;
}
export function readIntent(userId?: string, strict = false): TransferIntent | null {
  if (!userId) return null;
  try {
    const data = JSON.parse(sessionStorage.getItem(storageKey(userId)) ?? "null") as TransferIntent | null;
    if (data === null) return null;
    if (data && /^[a-f0-9-]{36}$/i.test(data.clientId) && /^[A-Z0-9_]+$/.test(data.provider) && (data.direction === "deposit" || data.direction === "withdrawal") && typeof data.amount === "string" && amountCents(data.amount) !== null) return data;
    if (strict) throw new Error("Invalid saved transfer intent");
    return null;
  } catch (reason) { if (strict) throw reason; return null; }
}
export function rememberIntent(userId: string, intent: TransferIntent | null) {
  if (intent) sessionStorage.setItem(storageKey(userId), JSON.stringify(intent));
  else sessionStorage.removeItem(storageKey(userId));
}
export function validTransfer(transfer: PublicWalletTransfer, userId: string, intent?: TransferIntent) {
  const resolution = transfer?.resolution;
  const validResolution = !resolution || (
    ["full", "partial", "failed", "needs_review"].includes(resolution.outcome)
    && Number.isSafeInteger(resolution.creditedAmountCents) && resolution.creditedAmountCents >= 0
    && Number.isSafeInteger(resolution.refundedAmountCents) && resolution.refundedAmountCents >= 0
    && (resolution.actualAmountCents === undefined || Number.isSafeInteger(resolution.actualAmountCents) && resolution.actualAmountCents >= 0)
    && typeof resolution.resolvedAt === "string" && Number.isFinite(Date.parse(resolution.resolvedAt))
  );
  return transfer && typeof transfer.id === "string" && transfer.userId === userId && Number.isSafeInteger(transfer.amountCents) && transfer.amountCents > 0 && Object.hasOwn(walletTransferLabels, transfer.status) && validResolution && (!intent || (transfer.provider === intent.provider && transfer.direction === intent.direction && transfer.amountCents === amountCents(intent.amount) && (!intent.orderId || transfer.id === intent.orderId)));
}
